import React, { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';
import type { LscLeague, LscVodCandidate } from '../../types';
import { lscPath } from '../../routing';
import { NAV_LABEL_HOME, OVERVIEW_QUICK_ACTIONS } from '../../leagueNav';
import LeagueStatusCards from '../LeagueStatusCards';
import ScheduleSyncStatusPanel from '../ScheduleSyncStatusPanel';
import CommissionerDigestCard from '../CommissionerDigestCard';
import LiveReadinessCard from '../LiveReadinessCard';
import LiveTruthStatusCard from '../LiveTruthStatusCard';
import PaidBetaCommandCenterSection from '../PaidBetaCommandCenterSection';
import LscCollapsibleSection from '../LscCollapsibleSection';

export type CommissionerCockpitProps = {
  league: LscLeague;
  leagueId: string;
  matchCount?: number;
  memberCount?: number;
  memberDetail?: string;
  candidateCount?: number;
  vodSyncReadyDetail?: string;
  archiveCount?: number;
  vodCandidates: LscVodCandidate[];
  vodCandidatesLoading: boolean;
  vodCandidatesError: string | null;
  defaultExpanded?: boolean;
  embedded?: boolean;
};

const CommissionerCockpit: React.FC<CommissionerCockpitProps> = ({
  league,
  leagueId,
  matchCount,
  memberCount,
  memberDetail,
  candidateCount,
  vodSyncReadyDetail,
  archiveCount,
  vodCandidates: _vodCandidates,
  vodCandidatesLoading: _vodCandidatesLoading,
  vodCandidatesError: _vodCandidatesError,
  defaultExpanded = false,
  embedded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || embedded);
  const [setupNeedsAttention, setSetupNeedsAttention] = useState(false);

  const handleSetupIncompleteChange = useCallback((incomplete: boolean) => {
    setSetupNeedsAttention(incomplete);
    if (incomplete) {
      setExpanded(true);
    }
  }, []);

  const body = (
    <div className="space-y-8">
      <PaidBetaCommandCenterSection
        leagueId={leagueId}
        memberCount={memberCount}
        matchCount={matchCount}
        archiveCount={archiveCount}
        candidateCount={candidateCount}
        onSetupIncompleteChange={handleSetupIncompleteChange}
      />

      <div className="flex flex-wrap gap-3" data-testid="commissioner-cockpit-quick-links">
        <Link to={lscPath(`/leagues/${leagueId}`)} className="btn-secondary">
          {NAV_LABEL_HOME}
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/members`)} className="btn-secondary">
          Members
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/import`)} className="btn-secondary">
          {OVERVIEW_QUICK_ACTIONS.importSync}
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/matches`)} className="btn-secondary">
          {OVERVIEW_QUICK_ACTIONS.leagueSchedules}
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/needs-attention`)} className="btn-secondary">
          Needs Attention
        </Link>
        <Link to={lscPath(`/leagues/${leagueId}/archive`)} className="btn-secondary">
          Archive
        </Link>
        <Link to={lscPath('/billing')} className="btn-secondary">
          Billing
        </Link>
        <Link
          to={lscPath(`/leagues/${leagueId}/settings`)}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Settings className="h-4 w-4" aria-hidden />
          Settings
        </Link>
      </div>

      <LeagueStatusCards
        league={league}
        leagueId={leagueId}
        matchCount={matchCount}
        memberCount={memberCount}
        memberDetail={memberDetail}
        candidateCount={candidateCount}
        vodSyncReadyDetail={vodSyncReadyDetail}
        archiveCount={archiveCount}
        isCommissioner
      />

      <LscCollapsibleSection
        title="Schedule sync"
        subtitle="When your league schedule was last refreshed."
        defaultExpanded={false}
        testId="command-center-schedule-sync"
      >
        <ScheduleSyncStatusPanel leagueId={leagueId} compact />
      </LscCollapsibleSection>

      <LscCollapsibleSection
        title="League status"
        subtitle="Recent sync activity and archive review counts."
        defaultExpanded={false}
        testId="command-center-league-status"
      >
        <CommissionerDigestCard leagueId={leagueId} hideTitle />
      </LscCollapsibleSection>

      <LscCollapsibleSection
        title="Live game detection"
        subtitle="Twitch live detection readiness and live stream checks."
        defaultExpanded={false}
        testId="command-center-live-detection"
      >
        <LiveReadinessCard leagueId={leagueId} />
        <LiveTruthStatusCard leagueId={leagueId} />
      </LscCollapsibleSection>
    </div>
  );

  if (embedded) {
    return (
      <div data-testid="commissioner-cockpit" className="space-y-8">
        {body}
      </div>
    );
  }

  return (
    <section
      className="rounded-xl border border-secondary-700 bg-secondary-900/60"
      data-testid="commissioner-cockpit"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-800 px-4 py-3">
        <div>
          <h2 className="text-base font-semibold text-white">Command Center</h2>
          <p className="text-xs text-secondary-500 mt-0.5">
            Setup, schedule sync, Needs Attention, and archive readiness.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          data-testid="commissioner-cockpit-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4" aria-hidden />
              Hide
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" aria-hidden />
              Show
            </>
          )}
        </button>
      </div>

      {expanded ? (
        <div className="p-4">{body}</div>
      ) : (
        <p className="px-4 py-3 text-xs text-secondary-500" data-testid="commissioner-cockpit-collapsed-hint">
          {setupNeedsAttention
            ? 'League setup needs attention — open Command Center for next steps.'
            : 'Needs Attention, schedule sync, and setup tools are available here.'}
        </p>
      )}
    </section>
  );
};

export default CommissionerCockpit;
